import React from 'react';
import PropTypes from 'prop-types';
import { CancelToken, isCancel } from 'axios';

export async function fetchSignals({
  dataService,
  doneCallback,
  errorCallback,
  orgID,
  outputSignalLimit,
  outputSignalPage,
  searchByName,
  signalLimit,
  signalPage,
  startCallback,
}) {
  let refineQueryMessage = { showNoSearchMessage: true };
  if (searchByName) {
    try {
      if (startCallback) startCallback();
      const params = {
        orgID,
        outputSignalLimit,
        outputSignalPage,
        searchByName,
        signalLimit,
        signalPage,
      };

      const source = CancelToken.source();
      const { data, outputSignalCount, signalCount } =
        await dataService.fetchSignalsData(params, source.token);

      const totalCount = outputSignalCount + signalCount;

      refineQueryMessage =
        totalCount > data.length
          ? {
              refineQueryMessage: `${
                totalCount - data.length
              } more signals match this search. Please refine your query.`,
            }
          : totalCount === 0
            ? { showNoResultsMessage: true }
            : {};

      if (doneCallback) doneCallback();

      return data
        .map(({ name, assetCount }) => {
          return { label: name, value: name, assetCount };
        })
        .concat(refineQueryMessage);
    } catch (err) {
      if (isCancel(err)) return;

      const error = err.response?.data?.message ?? err.message;
      if (errorCallback) errorCallback(error);

      return [{ error }];
    }
  }
  return [refineQueryMessage];
}

export function SignalsOption({ data, innerProps }) {
  return (
    <div className="d-flex align-items-center mt-3 w-100" {...innerProps}>
      {(() => {
        switch (true) {
          case !!data.refineQueryMessage:
            return (
              <span className="text-warning nine-tenth-text ml-3">
                {data.refineQueryMessage}
              </span>
            );
          case !!data.error:
            return (
              <span className="text-danger nine-tenth-text ml-3">
                {data.error}
              </span>
            );
          case !!data.value:
            return (
              <>
                <span className="ml-3 nine-tenth-text text-gray">
                  {data.value || ''}
                </span>
                <span className="three-quarter-text ml-4 text-icon-link-gray">{`Signal found on ${
                  data.assetCount || 0
                } Assets`}</span>{' '}
              </>
            );
          case data.showNoResultsMessage:
            return (
              <span className="mx-auto text-gray three-quarters-text mb-3">
                No signals match this search
              </span>
            );
          case data.showNoSearchMessage:
            return (
              <span className="mx-auto text-gray three-quarters-text mb-3">
                Start typing to search for a signal
              </span>
            );
          default:
            return null;
        }
      })()}
    </div>
  );
}

SignalsOption.propTypes = {
  innerProps: PropTypes.object,
  data: PropTypes.shape({
    value: PropTypes.string,
    assetCount: PropTypes.number,
    refineQueryMessage: PropTypes.string,
    showNoSearchMessage: PropTypes.bool,
    showNoResultsMessage: PropTypes.bool,
    error: PropTypes.string,
  }),
};

export function truncateValueFromString(value, precision) {
  const parsed = Number.parseFloat(value);
  if (!Number.isFinite(parsed)) return;

  return Number.isInteger(parsed)
    ? value
    : Number.parseFloat(parsed.toFixed(precision ?? 2));
}
